import type { ToastFn } from './primitives.js'

/**
 * TÉLÉCHARGER UN FICHIER FABRIQUÉ PAR LE SERVEUR (24/10/2026) — sans JSX.
 *
 * L'export CSV d'une soirée et celui d'une nuit de comptabilité sortent d'une
 * route GET authentifiée, qui décide seule des droits. Le navigateur ne fait
 * que récupérer le fichier et le proposer à l'enregistrement.
 *
 * ═══ POURQUOI PAS UN SIMPLE <a href download> ═══
 *
 * Sur un refus (401, 403, 422), le navigateur enregistrerait le JSON de
 * l'erreur sous le nom d'un .csv, sans un mot. Ici : `fetch` (cookie de la
 * session, `same-origin`), puis le fichier s'il est arrivé, sinon le message
 * du serveur dans un toast. L'export part au clic, jamais par un lien qu'un
 * aperçu pourrait précharger : chaque export écrit une ligne au journal.
 *
 * Le menu clic droit appellera la même fonction : il ne fait jamais `fetch`
 * lui-même.
 */

/**
 * Les adresses des deux exports CSV, écrites une fois : la fiche soirée, la
 * fiche de nuit et, plus tard, le menu clic droit les lisent d'ici. (La fiche
 * de nuit n'écrit aucune adresse `/api/v1/finance/nights/${…}` à la main : ses
 * lectures passent par `nights-common`.)
 */
export const urlExportCsvSoiree = (id: string): string => `/api/v1/soirees/${id}/export.csv`
export const urlExportCsvNuit = (date: string): string => `/api/v1/finance/nights/${date}/export.csv`

/** La session a expiré : le message du toast, pas une redirection au milieu d'un export. */
export const EXPORT_SESSION_EXPIREE = 'Votre session a expiré : reconnectez-vous pour exporter.'
/** Le serveur ne répond pas (coupure, délai). */
export const EXPORT_SANS_REPONSE = 'L’export n’a pas pu être téléchargé : le serveur ne répond pas. Réessayez.'
/** Un refus sans message lisible. */
export const EXPORT_REFUSE = 'L’export n’a pas pu être préparé. Réessayez dans un instant.'

/**
 * Le nom du fichier d'après l'en-tête Content-Disposition : la forme
 * étendue `filename*=UTF-8''…` d'abord, sinon `filename="…"`. `null` si
 * l'en-tête manque ou ne nomme rien. Une barre oblique ou un antislash ne
 * passent jamais : le nom reste un nom, pas un chemin.
 */
export function nomDepuisDisposition(h: string | null): string | null {
  if (!h) return null
  const etendu = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(h)
  let nom: string | null = null
  if (etendu?.[1]) {
    try { nom = decodeURIComponent(etendu[1].trim()) } catch { nom = null }
  }
  if (nom === null) {
    const simple = /filename\s*=\s*"([^"]*)"/i.exec(h) ?? /filename\s*=\s*([^;]+)/i.exec(h)
    nom = simple?.[1]?.trim() ?? null
  }
  if (nom === null) return null
  const propre = nom.replace(/[/\\\r\n"]/g, '_').trim()
  return propre === '' ? null : propre
}

/**
 * L'en-tête que la garde des exports exige (apps/api/src/routes/export-garde.ts) :
 * une navigation ou un lien ne savent pas le poser, seul le `fetch` de la page.
 */
export const EN_TETES_EXPORT: Record<string, string> = { 'x-gestion-export': '1' }

/**
 * Récupère `url` et propose le fichier à l'enregistrement. Rend `true` si
 * le fichier est parti, `false` sinon (le toast a dit pourquoi). `secours` :
 * le nom à défaut d'en-tête lisible.
 */
export async function telechargerFichier(url: string, secours: string, toast: ToastFn): Promise<boolean> {
  let r: Response
  try {
    r = await fetch(url, { credentials: 'same-origin', headers: EN_TETES_EXPORT })
  } catch {
    toast('error', EXPORT_SANS_REPONSE)
    return false
  }
  if (!r.ok) {
    if (r.status === 401) {
      toast('error', EXPORT_SESSION_EXPIREE)
      return false
    }
    // Le message du serveur, en français (« Soirée inconnue », « Cette nuit n’est pas une date valide. »…).
    const corps = (await r.json().catch(() => null)) as { message?: unknown } | null
    toast('error', typeof corps?.message === 'string' && corps.message !== '' ? corps.message : EXPORT_REFUSE)
    return false
  }
  let blob: Blob
  try {
    blob = await r.blob()
  } catch {
    toast('error', EXPORT_SANS_REPONSE)
    return false
  }
  const nom = nomDepuisDisposition(r.headers.get('content-disposition')) ?? secours
  const lien = document.createElement('a')
  lien.href = URL.createObjectURL(blob)
  lien.download = nom
  document.body.appendChild(lien)
  lien.click()
  lien.remove()
  // Révoquée APRÈS le départ du téléchargement : juste après click(), certains navigateurs l'annulaient.
  const adresse = lien.href
  setTimeout(() => URL.revokeObjectURL(adresse), 1000)
  toast('ok', `Export prêt : ${nom}`)
  return true
}
